import React, { FC } from 'react';
import ReactDOM from 'react-dom';
import { createBrowserHistory, History } from 'history';
import { Container } from 'inversify';
import singleSpaReact from 'single-spa-react';
import { I18nextProvider } from 'react-i18next';
import { Router } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { ApolloClient, ApolloProvider, NormalizedCacheObject } from '@apollo/client';
import { createIOC } from './bindings';
import { iocNames } from './iocNames';
import { RootProps, RootUpdatable } from './types/RootUpdatable';
import { I18Service } from './services/I18Service';
import { Root } from './components/Root';
import { AppContext } from '@unitedtraders/microfrontend-common';

const packageInfo = require('../package.json');

function ErrorFallback({ error, resetErrorBoundary }: any) {
  return (
    <div role="alert">
      <p>Something went wrong:</p>
      <pre>{error.message}</pre>
      <button onClick={resetErrorBoundary}>Try again</button>
    </div>
  );
}

let ioc: Container;

const RootComponent: FC = () => {
  const { i18next } = ioc.get<I18Service>(iocNames.i18nService);
  const history = ioc.get<History>(iocNames.history);
  const apolloClient = ioc.get<ApolloClient<NormalizedCacheObject>>(iocNames.apolloClient);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <AppContext.Provider value={ioc}>
        <ApolloProvider client={apolloClient}>
          <I18nextProvider i18n={i18next}>
            <Router history={history}>
              <Root />
            </Router>
          </I18nextProvider>
        </ApolloProvider>
      </AppContext.Provider>
    </ErrorBoundary>
  );
};

function updateIOC(props: RootProps) {
  const services = ioc.getAll<RootUpdatable>(iocNames.rootUpdatable);
  services.forEach((service) => {
    service.update(props);
  });
}

const reactLifecycles = singleSpaReact({
  // @ts-ignore
  React,
  ReactDOM,
  rootComponent: RootComponent,
});

export const bootstrap = reactLifecycles.bootstrap;

export const mount = (props: RootProps) => {
  console.groupCollapsed('Application withdraw started');
  console.info('VERSION', packageInfo.version);
  console.groupEnd();

  const history = createBrowserHistory({ basename: props.basename });
  ioc = createIOC({ history, ...props });

  updateIOC(props);

  return reactLifecycles.mount(props);
};

export const unmount = reactLifecycles.unmount;

export const update = (props: RootProps) => {
  updateIOC(props);

  return reactLifecycles.update!(props);
};

export type Props = RootProps;
